// src/components/rooms/RoomList.jsx
import React from 'react';
import { FiEdit2, FiTrash2, FiUsers, FiMapPin, FiClock } from 'react-icons/fi';

const getStatusColor = (status) => {
  switch (status) {
    case 'available': return 'bg-green-100 text-green-800';
    case 'occupied': return 'bg-red-100 text-red-800';
    case 'maintenance': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'treatment': return <FiMapPin className="h-4 w-4" />;
    case 'consultation': return <FiUsers className="h-4 w-4" />;
    case 'group': return <FiUsers className="h-4 w-4" />;
    case 'massage': return <FiClock className="h-4 w-4" />;
    default: return <FiMapPin className="h-4 w-4" />;
  }
};

export default function RoomList({ rooms, onEdit, onDelete, onRoomClick, loading }) {
  if (rooms.length === 0) {
    return (
      <div className="card-modern">
        <div className="text-center py-12">
          <FiMapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No rooms available</h3>
          <p className="text-gray-500 mb-6">Add your first room to get started with room management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-modern overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Room List</h3>
        <p className="text-sm text-gray-500 mt-1">{rooms.length} room{rooms.length !== 1 ? 's' : ''} configured</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        {rooms.map(room => (
          <div 
            key={room.id} 
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onRoomClick && onRoomClick(room)}
          >
            {/* Room Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-blue-500 bg-opacity-10">
                  {getTypeIcon(room.type)}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{room.name}</h4>
                  <p className="text-sm text-gray-500 capitalize">{room.type || 'General'} Room</p>
                </div>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(room.status)}`}>
                {room.status || 'available'}
              </span>
            </div>

            {/* Room Details */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Capacity</span>
                <div className="flex items-center space-x-1">
                  <FiUsers className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">{room.capacity} people</span>
                </div>
              </div>
              
              {room.description && (
                <div>
                  <span className="text-sm text-gray-600">Description</span>
                  <p className="text-sm text-gray-900 mt-1">{room.description}</p>
                </div>
              )}
              
              {room.equipment && Array.isArray(room.equipment) && room.equipment.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600">Equipment</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {room.equipment.slice(0, 3).map((item, index) => (
                      <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {item}
                      </span>
                    ))}
                    {room.equipment.length > 3 && (
                      <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        +{room.equipment.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                {room.updatedAt ? `Updated ${new Date(room.updatedAt).toLocaleDateString()}` : 'Recently added'}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(room);
                  }}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  <FiEdit2 className="h-3 w-3 mr-1" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(room.id);
                  }}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  <FiTrash2 className="h-3 w-3 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
